import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { DASHBOARD_PACIFIC_TZ } from '../../../utils/dashboardPacificTime';

// —— 指定查询时刻（三连击头部时钟打开，按美西时间输入），同派送大屏 AsOfModal —— //

// DatePicker 以浏览器本地时区承载「美西墙钟」，提交时再按美西时区解释回 epoch ms
const toPacificWallClock = (ms: number): Dayjs =>
    dayjs(dayjs(ms).tz(DASHBOARD_PACIFIC_TZ).format('YYYY-MM-DD HH:mm:ss'));
const fromPacificWallClock = (value: Dayjs): number =>
    dayjs.tz(value.format('YYYY-MM-DD HH:mm:ss'), DASHBOARD_PACIFIC_TZ).valueOf();
// 后端只接受近 30 天：禁选美西今天之后、29 天前之前的日期，选得到的任意时刻都不早于 30 天前
const AS_OF_LOOKBACK_DAYS = 29;
const isAsOfDateDisabled = (d: Dayjs): boolean => {
    const today = toPacificWallClock(Date.now());
    return d.isAfter(today, 'day') || d.isBefore(today.subtract(AS_OF_LOOKBACK_DAYS, 'day'), 'day');
};

interface TrackingAsOfModalProps {
    open: boolean;
    asOfMs: number | null;
    getContainer: () => HTMLElement;
    onConfirm: (asOfMs: number) => void;
    onReset: () => void;
    onClose: () => void;
}

export const TrackingAsOfModal: React.FC<TrackingAsOfModalProps> = ({
    open,
    asOfMs,
    getContainer,
    onConfirm,
    onReset,
    onClose,
}) => {
    const { t } = useTranslation();
    const [value, setValue] = useState<Dayjs | null>(null);
    useEffect(() => {
        if (open) setValue(toPacificWallClock(asOfMs ?? Date.now()));
    }, [open, asOfMs]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={t('pickup.driverTracking.clock.asOfTitle')}
            getContainer={getContainer}
            width={360}
            destroyOnClose
            footer={[
                <Button key="reset" onClick={onReset} disabled={asOfMs === null}>
                    {t('pickup.driverTracking.clock.asOfReset')}
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    disabled={!value}
                    onClick={() => value && onConfirm(fromPacificWallClock(value))}
                >
                    {t('pickup.driverTracking.clock.asOfConfirm')}
                </Button>,
            ]}
        >
            <DatePicker
                showTime
                allowClear={false}
                value={value}
                onChange={setValue}
                disabledDate={isAsOfDateDisabled}
                style={{ width: '100%' }}
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
            />
        </Modal>
    );
};
